import './AppNav.css'

import { clsx } from 'clsx'
import type { FC, ReactElement } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'

import { CaretDown } from '#design-system/icons/caret-down'
import { CaretRight } from '#design-system/icons/caret-right'
import type { IconProps } from '#design-system/icons/factory'

/** An icon component from the design-system icon set (e.g. `Package`, `Hexagon`). */
export type NavIcon = FC<IconProps>

/* Placement shapes — to be replaced with the real API data format once the data model is finalised.
 * The leading icon is REQUIRED on the top two tiers (apps + pages) and ABSENT on the inner tier
 * (sub-pages); the level-distinct types enforce that, so the data model cannot express an icon-less
 * app/page or an icon-bearing sub-page. Sub-pages are always leaves. */
export interface NavSubPage {
  id: string
  name: string
}

export interface NavPage {
  id: string
  name: string
  icon: NavIcon
  children?: NavSubPage[] | undefined
}

export interface NavApp {
  id: string
  name: string
  icon: NavIcon
  children?: NavPage[] | undefined
}

type NavTier = 'app' | 'page' | 'subpage'

/** A node's route is its id path under Home — the same scheme `buildNavRoutes` uses, so links and
 * routes match by construction. */
const homeHref = (ids: string[]): string => `/home/${ids.join('/')}`

interface NavRowProps {
  tier: NavTier
  name: string
  to: string
  /** Required on apps + pages, omitted on sub-pages. */
  icon?: NavIcon | undefined
  /** Defined only on branches (drives the caret + `aria-expanded`); presence ⇒ has children. */
  isExpanded?: boolean | undefined
  onToggle?: (() => void) | undefined
}

/** One `<li>` row: a full-height `<NavLink>` (the whole row is the click target; selection is the
 * link's `aria-current`) preceded, on branches, by a full-height square disclosure toggle whose
 * icon swaps to a caret on hover. */
const NavRow = ({
  tier,
  name,
  to,
  icon: Icon,
  isExpanded,
  onToggle,
}: NavRowProps): ReactElement => {
  const { t } = useTranslation()
  const Caret = isExpanded === true ? CaretDown : CaretRight

  return (
    <li className={clsx('row', tier, onToggle !== undefined && 'has-children')}>
      {onToggle !== undefined && (
        <button
          aria-expanded={isExpanded}
          aria-label={
            isExpanded === true
              ? t('shell.nav.collapse', { name })
              : t('shell.nav.expand', { name })
          }
          className="disclosure"
          onClick={onToggle}
          type="button"
        >
          {Icon !== undefined ? <Icon className="icon" /> : null}
          <Caret className="caret" />
        </button>
      )}
      <NavLink className="link" end to={to}>
        {onToggle === undefined && Icon !== undefined && (
          <span className="leading">
            <Icon className="icon" />
          </span>
        )}
        <span className="label">{name}</span>
      </NavLink>
    </li>
  )
}

/** Every app + page that has children — seeds the default-expanded state (matches the mock). */
const collectExpandableIds = (tree: NavApp[]): Set<string> => {
  const ids = new Set<string>()
  for (const app of tree) {
    if ((app.children?.length ?? 0) === 0) {
      continue
    }
    ids.add(app.id)
    for (const page of app.children ?? []) {
      if ((page.children?.length ?? 0) > 0) {
        ids.add(page.id)
      }
    }
  }
  return ids
}

interface AppNavProps {
  tree: NavApp[]
  className?: string | undefined
}

/** Home-tab navigation: a nestable app → page → sub-page tree. Rendered recursively per tier but
 * flattened (`flatMap`) into one `<ul>` of `<li>` rows — depth shown by per-row indent. */
export const AppNav = ({ tree, className }: AppNavProps): ReactElement => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => collectExpandableIds(tree))

  const toggle = (id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const renderSubPage = (subPage: NavSubPage, ancestorIds: string[]): ReactElement[] => [
    <NavRow
      key={subPage.id}
      name={subPage.name}
      tier="subpage"
      to={homeHref([...ancestorIds, subPage.id])}
    />,
  ]

  const renderPage = (page: NavPage, ancestorIds: string[]): ReactElement[] => {
    const ids = [...ancestorIds, page.id]
    const children = page.children ?? []
    const isExpanded = expandedIds.has(page.id)
    return [
      <NavRow
        key={page.id}
        icon={page.icon}
        isExpanded={children.length > 0 ? isExpanded : undefined}
        name={page.name}
        onToggle={
          children.length > 0
            ? () => {
                toggle(page.id)
              }
            : undefined
        }
        tier="page"
        to={homeHref(ids)}
      />,
      ...(isExpanded ? children.flatMap((subPage) => renderSubPage(subPage, ids)) : []),
    ]
  }

  const renderApp = (app: NavApp): ReactElement[] => {
    const ids = [app.id]
    const children = app.children ?? []
    const isExpanded = expandedIds.has(app.id)
    return [
      <NavRow
        key={app.id}
        icon={app.icon}
        isExpanded={children.length > 0 ? isExpanded : undefined}
        name={app.name}
        onToggle={
          children.length > 0
            ? () => {
                toggle(app.id)
              }
            : undefined
        }
        tier="app"
        to={homeHref(ids)}
      />,
      ...(isExpanded ? children.flatMap((page) => renderPage(page, ids)) : []),
    ]
  }

  return <ul className={clsx('app-nav', className)}>{tree.flatMap((app) => renderApp(app))}</ul>
}
